import * as React from "react"
// import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

interface AccordionItemProps {
  value: string
  children: React.ReactNode
}

interface AccordionTriggerProps {
  children: React.ReactNode
  className?: string
}

interface AccordionContentProps {
  children: React.ReactNode
  className?: string
}

interface AccordionContextType {
  openItems: string[]
  toggleItem: (value: string) => void
}

const AccordionContext = React.createContext<AccordionContextType | undefined>(undefined)

const useAccordion = () => {
  const context = React.useContext(AccordionContext)
  if (!context) {
    throw new Error("Accordion components must be used within Accordion")
  }
  return context
}

const AccordionItemContext = React.createContext<string | undefined>(undefined)

const useAccordionItem = () => {
  const context = React.useContext(AccordionItemContext)
  if (!context) {
    throw new Error("AccordionTrigger and AccordionContent must be used within AccordionItem")
  }
  return context
}

interface AccordionProps {
  children: React.ReactNode
  type?: "single" | "multiple"
  defaultValue?: string | string[]
  className?: string
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ children, type = "single", defaultValue, className }, ref) => {
    const [openItems, setOpenItems] = React.useState<string[]>(() => {
      if (defaultValue) {
        return Array.isArray(defaultValue) ? defaultValue : [defaultValue]
      }
      return []
    })

    const toggleItem = React.useCallback(
      (value: string) => {
        setOpenItems((prev) => {
          if (type === "single") {
            return prev.includes(value) ? [] : [value]
          } else {
            return prev.includes(value)
              ? prev.filter((item) => item !== value)
              : [...prev, value]
          }
        })
      },
      [type]
    )

    return (
      <AccordionContext.Provider value={{ openItems, toggleItem }}>
        <div ref={ref} className={cn("space-y-4", className)}>
          {children}
        </div>
      </AccordionContext.Provider>
    )
  }
)
Accordion.displayName = "Accordion"

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ value, children }, ref) => {
    return (
      <AccordionItemContext.Provider value={value}>
        <div ref={ref} className="border-b border-gray-200">
          {children}
        </div>
      </AccordionItemContext.Provider>
    )
  }
)
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className }, ref) => {
    const { openItems, toggleItem } = useAccordion()
    const value = useAccordionItem()
    const isOpen = openItems.includes(value)

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => toggleItem(value)}
        className={cn(
          "flex w-full items-center justify-between py-6 text-left font-medium transition-all hover:opacity-70",
          className
        )}
      >
        {children}
        <div
          className={cn(
            "rounded-full w-10 h-10 flex items-center justify-center transition-all",
            isOpen ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-700"
          )}
        >
          {isOpen ? (
            <span className="text-2xl font-light leading-none">−</span>
          ) : (
            <span className="text-2xl font-light leading-none">+</span>
          )}
        </div>
      </button>
    )
  }
)
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className }, ref) => {
    const { openItems } = useAccordion()
    const value = useAccordionItem()
    const isOpen = openItems.includes(value)

    return (
      <div
        ref={ref}
        className={cn(
          "overflow-hidden transition-all",
          isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className={cn("pb-6 pt-0 text-gray-600", className)}>
          {children}
        </div>
      </div>
    )
  }
)
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
